import type { Baseline } from '@shared/models/project.model';
import type { Action } from '../update-engine.models';
import { replaceProperties } from '../../object.helpers';
import { getItemById } from '@shared/helpers/array.helpers';

/**
 * Apply a partial update (e.g. rename, replace inner changes) to a single {@link Action} on
 * a {@link Baseline}, identified by id.
 *
 * Copies only the touched action slot so sibling actions keep their references — important
 * for downstream memoization that uses identity to detect changed actions.
 *
 * Returns the same {@link version} reference when the action is missing or the partial
 * does not introduce any field-level change ({@link replaceProperties} short-circuits
 * shallow-equal updates).
 *
 * @param version - Current version state (read-only)
 * @param actionId - {@link Action.id} of the action to update
 * @param partial - Partial action fields to overlay (id is fixed by `actionId`)
 */
export const updateAction = (
  version: Baseline,
  actionId: string,
  partial: Partial<Omit<Action, 'id'>>,
): Baseline => {
  const actions = version.actions;
  if (!actions?.length) return version;

  const target = getItemById(actions, actionId);

  if (!target) return version;

  const next = replaceProperties<Action>(target, partial);

  // No changes were made, return the original version
  if (next === target) return version;

  // Update the action in the actions array
  return {
    ...version,
    actions: actions.map((action) => (action.id === actionId ? next : action)),
  };
};
